import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import ContractPreview from '../components/ContractPreview';
import LoanDetails from '../components/Forms/LoanDetails';
import CameraCapture from '../components/CameraCapture';
import InvoiceModal from '../components/InvoiceModal';
import { Line } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler } from 'chart.js';
import Button from '../components/UI/Button';
import ActionButton from '../components/UI/ActionButton';
import NextPaymentCard from '../components/NextPaymentCard';
import ApplicationStepper from '../components/ApplicationStepper';
import SentBackBanner from '../components/SentBackBanner';
import DueAlertsStrip from '../components/DueAlertsStrip';
import SearchBox from '../components/SearchBox';
import DocumentsHubCard from '../components/DocumentsHubCard';
import SavedApplicationCard from '../components/SavedApplicationCard';
import FullScreenPanel from '../components/UI/FullScreenPanel';
import ApplyForLoanCard from '../components/ApplyForLoanCard';
import { CurrentLoanCard, NoPaymentCard, LoanSummaryCard } from '../components/DashboardAltCards';
import LoanApplication from './LoanApplication';
import { useLocation, useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { enrichLoanForInvoice as enrichInvoiceLoan, renderInvoiceHtml } from '../utils/invoiceUtils';
import { formatDateTimeDMY, formatDateDMY } from '../utils/dateUtils';
import { openDocument } from '../utils/documentLinks';
import { clickableProps, LINK_TEXT } from '../utils/clickable';
import ChartCard from '../components/Admin/ChartCard';
import { toCurrency } from '../utils/adminMetrics';
import { useSortableTable, TABLE_HEAD_ROW, TABLE_HEAD_TEXT, TABLE_ROW_LINE } from '../components/UI/tableSort';
import { ActionsCell } from '../components/UI/ActionsMenu';
import StatusBadge from '../components/UI/StatusBadge';
import Pager from '../components/UI/Pager';
import DocumentTile, { DOCUMENT_GRID } from '../components/UI/DocumentTile';

// The loans table's header cells (the right-hand ones hold amounts).
const LOAN_TH = { className: `px-3 py-2 ${TABLE_HEAD_TEXT} whitespace-nowrap` };
const LOAN_TH_RIGHT = { ...LOAN_TH, align: 'right' };

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const PAGE_SIZE = 2;

// Small helper component to render client notes and attached documents in the contract modal header
// loanActions: the loan's state.actions from the API (what the client may do now).
// part: 'notes' — the staff's message, shown above the review; 'documents' —
// the loan's documents (view, download, replace, retake the photo), shown
// below it. The review itself is the same ContractPreview as at first signing.
const ClientDocumentsAndNotes = ({ loanId, clientId, loanActions = {}, requiredPhotoFile, requiredPhotoPreview, setRequiredPhotoFile, setRequiredPhotoPreview, part = 'all' }) => {
  const [docs, setDocs] = useState([]);
  const [notes, setNotes] = useState([]);
  const [uploadingType, setUploadingType] = useState(null);
  // A new live photo taken on this screen (shows "Photo captured successfully").
  const [photoTaken, setPhotoTaken] = useState(false);
  useEffect(() => {
  const fetchData = async () => {
      const token = localStorage.getItem('token');
      if (!token || !loanId) return;
      try {
        const [loanRes, clientRes] = await Promise.all([
          fetch(apiUrl(`/api/document/loan/${loanId}`), { headers: { 'Authorization': `Bearer ${token}` } }),
          clientId
            ? fetch(apiUrl(`/api/document/client/${clientId}`), { headers: { 'Authorization': `Bearer ${token}` } })
            : Promise.resolve({ ok: true, json: async () => [] })
        ]);
        let loanDocs = [];
        if (loanRes.ok) {
          const j = await loanRes.json();
          loanDocs = (j.documents || []).map(doc => ({ ...doc, document_scope: 'loan' }));
        }
        let clientDocs = [];
        if (clientRes.ok) {
          const cd = await clientRes.json();
          clientDocs = (Array.isArray(cd) ? cd : []).filter(doc => {
            const type = (doc.document_type || '').toLowerCase();
            return type.includes('id') || type.includes('identification') || type.includes('passport');
          }).map(doc => ({ ...doc, document_scope: 'client-id' }));
        }
        setDocs([...clientDocs, ...loanDocs]);
      } catch (e) { console.warn('Failed to fetch docs', e); }
      try {
  const nRes = await fetch(apiUrl(`/api/loans/${loanId}/notes`), { headers: { 'Authorization': `Bearer ${token}` } });
        if (nRes.ok) {
          const nj = await nRes.json();
          const notesArray = Array.isArray(nj) ? nj : (Array.isArray(nj.notes) ? nj.notes : []);
          setNotes(notesArray || []);
        }
      } catch (e) { console.warn('Failed to fetch notes', e); }
    };
    fetchData();
  }, [loanId, clientId]);

  const refresh = async () => {
    const token = localStorage.getItem('token');
    if (!token || !loanId) return;
    try {
      const [loanRes, clientRes] = await Promise.all([
        fetch(apiUrl(`/api/document/loan/${loanId}`), { headers: { 'Authorization': `Bearer ${token}` } }),
        clientId ? fetch(apiUrl(`/api/document/client/${clientId}`), { headers: { 'Authorization': `Bearer ${token}` } }) : Promise.resolve({ ok: true, json: async () => [] })
      ]);
      let loanDocs = [];
      if (loanRes.ok) {
        const j = await loanRes.json();
        loanDocs = (j.documents || []).map(doc => ({ ...doc, document_scope: 'loan' }));
      }
      let clientDocs = [];
      if (clientRes.ok) {
        const cd = await clientRes.json();
        clientDocs = (Array.isArray(cd) ? cd : []).filter(doc => {
          const type = (doc.document_type || '').toLowerCase();
          return type.includes('id') || type.includes('identification') || type.includes('passport');
        }).map(doc => ({ ...doc, document_scope: 'client-id' }));
      }
      setDocs([...clientDocs, ...loanDocs]);
    } catch (e) { console.warn('Failed to refresh docs', e); }
  };

  const uploadFile = async (file, docType) => {
    if (!clientId || !file) return;
    setUploadingType(docType || 'Document');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('contract', file);
      
      // For client-level documents (like Identification), don't include loan_id
      const isClientDocument = ['Identification', 'Proof of Residence', 'Proof of Address', 'POR'].includes(docType);
      
      if (!isClientDocument && loanId) {
        formData.append('loan_id', loanId);
      }
      
      formData.append('client_id', clientId);
      formData.append('document_type', docType || 'Document');

  const res = await fetch(apiUrl('/api/document/upload'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) {
        // Show the API's reason (e.g. DOCUMENTS_LOCKED) as it is.
        let apiMsg = '';
        try { apiMsg = (await res.json())?.error || ''; } catch {}
        throw new Error(apiMsg || 'Upload failed');
      }
      await refresh();
    } catch (err) {
      console.error('Error uploading document:', err);
      alert(err?.message || 'Upload failed');
    } finally {
      setUploadingType(null);
    }
  };

  // Helper for the mandatory add-photo capture (used in Pending mode)
  const onPhotoCaptured = (file, dataUrl) => {
    if (typeof setRequiredPhotoFile === 'function') setRequiredPhotoFile(file);
    if (typeof setRequiredPhotoPreview === 'function') setRequiredPhotoPreview(dataUrl || null);
  };

  if (!loanId) return null;
  const showNotes = part === 'all' || part === 'notes';
  const showPhoto = part === 'all' || part === 'photo';
  const showDocs = part === 'all' || part === 'documents';
  // The live photo is retaken in its own row under the cards, so it does
  // not make its card (and the whole row) taller than the others.
  const retakePhoto = docs.find((d) => {
    const isImg = d.mime_type ? d.mime_type.startsWith('image/') : /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(d.file_path || '');
    return isImg && d.document_scope === 'loan' && d.client_can_replace === true;
  });

  // The photo part on its own renders nothing when no photo is needed, so
  // its place under Additional Expenses collapses.
  if (part === 'photo') {
    const canSign = loanActions?.sign === true || loanActions?.resubmit === true;
    const hasPhoto = docs.some((d) => String(d.document_type || '').toLowerCase() === 'online client photo');
    const noteAsksForPhoto = notes.some((n) => /photo|selfie|picture|image|face/i.test(String(n.note_text || n.note || '')));
    if (!canSign || (hasPhoto && !noteAsksForPhoto)) return null;
  }

  return (
    <div className={part === 'photo' ? '' : showNotes && !showDocs ? 'mb-4' : 'mb-6'}>
      {showNotes && notes && notes.length > 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-300 rounded">
          {notes.map(n => (
            <div key={n.id} className="text-sm text-gray-800 dark:text-slate-200 mb-2">
              <strong className="block text-xs text-gray-600 dark:text-slate-400">Message from staff:</strong>
              <div dangerouslySetInnerHTML={{ __html: n.note || n.note_text || '' }} />
              <div className="text-xs text-gray-500 dark:text-slate-500 mt-1">{n.created_at ? formatDateTimeDMY(n.created_at) : ''}</div>
            </div>
          ))}
        </div>
      )}

  {showDocs && docs && docs.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold mb-3 text-gray-800 dark:text-slate-200">Your documents</h3>
          <div className={DOCUMENT_GRID}>
            {docs.map((doc) => {
              const isImage = doc.mime_type ? doc.mime_type.startsWith('image/') : /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(doc.file_path || '');
              const docType = (doc.document_type || '').toLowerCase();
              const isProtectedClientDoc = doc.document_scope === 'client-id' && (
                docType.includes('identification') || docType === 'id' || docType.includes('passport')
                || docType.includes('proof of residence') || docType.includes('proof of address') || docType === 'por'
              );
              // Loan documents say themselves whether the client may replace
              // them (client_can_replace, from the API); client-level ones follow
              // the loan's state.actions.replace_documents.
              const canReplace = doc.document_scope === 'loan'
                ? doc.client_can_replace === true
                : (loanActions?.replace_documents === true && !isProtectedClientDoc);
              const replaceKey = doc.document_type || (isImage ? 'Photo' : 'Document');
              return (
                <DocumentTile
                  key={doc.id}
                  type={doc.document_type || 'Document'}
                  doc={doc}
                  badge={doc.document_scope === 'client-id' ? 'Client' : undefined}
                  onPick={canReplace && !isImage ? (f) => uploadFile(f, replaceKey) : undefined}
                  uploading={uploadingType === replaceKey}
                />
              );
            })}
          </div>
          {retakePhoto && (
            <div className="mt-3 max-w-md">
              <CameraCapture
                buttonText="Retake photo"
                onPhotoCapture={(file) => { if (file) uploadFile(file, retakePhoto.document_type || 'Photo'); }}
              />
            </div>
          )}
        </div>
      )}

      {/* The live photo, as on the original application ("Client Photo ID",
          Take ID Photo). A loan with one already on file can have it
          recaptured: the new photo replaces it at once. A loan without one
          gets it with the signature. */}
      {showPhoto && (loanActions?.sign === true || loanActions?.resubmit === true) && (() => {
        const hasPhoto = docs.some((d) => String(d.document_type || '').toLowerCase() === 'online client photo');
        // Only when it is needed: the loan has no live photo (it is required
        // to sign), or staff's note asks for a new one.
        const noteAsksForPhoto = notes.some((n) => /photo|selfie|picture|image|face/i.test(String(n.note_text || n.note || '')));
        if (hasPhoto && !noteAsksForPhoto) return null;
        return (
          <div className="mb-6">
            <label className="block text-center text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Client Photo ID</label>
            <div onClick={(e) => e.preventDefault()}>
              <CameraCapture
                buttonText={hasPhoto ? 'Retake ID Photo' : 'Take ID Photo'}
                onPhotoCapture={async (file, imageUrl) => {
                  if (!file) return;
                  if (hasPhoto) {
                    await uploadFile(file, 'Online Client Photo');
                  } else {
                    onPhotoCaptured(file, imageUrl);
                  }
                  setPhotoTaken(true);
                }}
              />
            </div>
            {uploadingType === 'Online Client Photo' && (
              <p className="mt-2 text-xs text-gray-500 dark:text-slate-400 text-center">Saving your photo…</p>
            )}
            {photoTaken && uploadingType !== 'Online Client Photo' && (
              <div className="mt-2 p-2 bg-white dark:bg-slate-800 rounded border dark:border-slate-700">
                <span className="text-sm text-gray-600 dark:text-slate-400">Photo captured successfully</span>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

// Heroicons SVGs for section icons (same as AdminDashboard)
const UserIcon = () => (
  <svg className="w-6 h-6 text-blue-600 mr-2 inline-block" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <circle cx="12" cy="8" r="4" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 20v-2a6 6 0 0112 0v2" />
  </svg>
);
const CashIcon = () => (
  <svg className="w-6 h-6 text-yellow-600 mr-2 inline-block" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <rect width="20" height="12" x="2" y="6" rx="2" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const RepaymentIcon = () => (
  <svg className="w-6 h-6 text-green-600 mr-2 inline-block" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3" />
    <circle cx="12" cy="12" r="10" />
  </svg>
);

// Loan Details Popout - render the styled LoanDetails component (client view)
const LoanDetailsPopout = ({ loan, onClose }) => {
  if (!loan) return null;
  // Use the existing LoanDetails component which provides the formatted UI
  return (
    <LoanDetails
      loan={loan}
      onClose={onClose}
      // Clients don't have approve/reject permissions; pass defaults
      canApprove={false}
      canReject={false}
      canRevertToPending={false}
      userRole={'client'}
    />
  );
};

const ClientDashboard = () => {
  const [loans, setLoans] = useState([]);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [actionMessage, setActionMessage] = useState('');
  const [actionType, setActionType] = useState('info');
  const [showLoanDetails, setShowLoanDetails] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  // Inline loan-details / invoice panel renders right below the alerts row;
  // scroll it into view whenever it opens.
  const detailsPanelRef = useRef(null);
  const [invoiceLoan, setInvoiceLoan] = useState(null);
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedLoanForContract, setSelectedLoanForContract] = useState(null);
  const [isGeneratingContract, setIsGeneratingContract] = useState(false);
  const [contractMessage, setContractMessage] = useState('');
  // Every message here is an error (a missing detail or the API's refusal):
  // it shows in red and is brought into view, as it sits above the loan.
  const contractMessageRef = useRef(null);
  useEffect(() => {
    if (contractMessage && contractMessageRef.current) {
      contractMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [contractMessage]);
  const [clientProfile, setClientProfile] = useState(null);
  const [lastFetchedClient, setLastFetchedClient] = useState(null);

  // Bring the inline details/invoice panel into view when it opens.
  useEffect(() => {
    if ((showLoanDetails && selectedLoan) || (showInvoiceModal && invoiceLoan)) {
      setTimeout(() => {
        detailsPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }
  }, [showLoanDetails, selectedLoan, showInvoiceModal, invoiceLoan]);

  // Persist dashboard view preferences
  const [searchTerm, setSearchTerm] = useState('');
  const [requiredPhotoFile, setRequiredPhotoFile] = useState(null);
  const [requiredPhotoPreview, setRequiredPhotoPreview] = useState(null);
  const [references, setReferences] = useState({
    reference1_name: '',
    reference1_tel: '',
    reference2_name: '',
    reference2_tel: ''
  });
  // Loans are listed newest first; only search and paging are offered.
  const sortBy = { key: 'id', dir: 'desc' };
  useEffect(() => {
    try { localStorage.removeItem('dash.filterStatus'); localStorage.removeItem('dash.sortBy'); } catch {}
  }, []);

  // Distinctive row background colors by status (desktop table)
  const getRowBg = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'pending':
        return 'bg-blue-50';
      case 'approved':
        return 'bg-green-50';
      case 'awaiting client':
        return 'bg-indigo-50';
      case 'awaiting staff':
        return 'bg-purple-50';
      case 'outstanding':
        return 'bg-yellow-50';
      case 'paid off':
        return 'bg-emerald-50';
      case 'rejected':
      case 'cancelled':
        return 'bg-red-50';
      default:
        return '';
    }
  };

  // The loan application opens in a panel over the dashboard; closing it
  // reloads the loans (and the saved-application card) to show what changed.
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyResume, setApplyResume] = useState(false);
  const [loansRefreshKey, setLoansRefreshKey] = useState(0);
  const openApply = (resume = false) => { setApplyResume(resume); setApplyOpen(true); };
  const closeApply = () => { setApplyOpen(false); setApplyResume(false); setLoansRefreshKey((k) => k + 1); };
  const routerLocation = useLocation();
  const routerNavigate = useNavigate();
  // /dashboard?apply=1 (or =resume, from reminder emails) opens it directly.
  useEffect(() => {
    const apply = new URLSearchParams(routerLocation.search).get('apply');
    if (!apply) return;
    openApply(apply === 'resume');
    routerNavigate('/dashboard', { replace: true });
  }, [routerLocation.search]);

  // The client's position, worked out by the API (GET /api/loans/my-summary):
  // whether they can apply, totals, the next instalment, overdue loans.
  const [summary, setSummary] = useState(null);

  // The loans (each with the API's state) and the summary, loaded together.
  const loadLoans = async () => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    const [loansRes, summaryRes] = await Promise.all([
      axios.get(apiUrl('/api/loans/my-loans'), { headers }),
      axios.get(apiUrl('/api/loans/my-summary'), { headers }),
    ]);
    setLoans(loansRes.data);
    setSummary(summaryRes.data || null);
  };

  useEffect(() => {
    const fetchLoans = async () => {
      try {
        await loadLoans();
        setError('');
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to fetch loans');
      }
    };
    fetchLoans();
  }, [loansRefreshKey]);

  // Stat cards: the API's totals. They count approved loans and their
  // post-approval statuses only (loan.state.counts_in_stats).
  const totalLoans = Number(summary?.totals?.loans) || 0;
  const totalAmount = Number(summary?.totals?.borrowed) || 0;
  const totalOutstanding = Number(summary?.totals?.outstanding) || 0;
  const outstandingLoans = loans.filter(l => l.state?.counts_in_stats && l.state?.is_active);
  // The charts use the same loans as the tiles (state.counts_in_stats),
  // placed in the month they were paid out (paid_out_on).
  const statsLoans = loans.filter(l => l.state?.counts_in_stats);

  // Chart helpers
  const getLast12Months = () => {
    const months = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(d.toLocaleString('default', { month: 'short', year: '2-digit' }));
    }
    return months;
  };

  const monthLabels = getLast12Months();

  // 'YYYY-MM-DD' (paid_out_on) read as a local date so the month is right.
  const toDate = (v) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v || ''));
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(v);
  };

  const getMonthlyCounts = (items, dateField, filterFn) => {
    const counts = Array(12).fill(0);
    const now = new Date();
    items.forEach(item => {
      if (!item[dateField]) return;
      const date = toDate(item[dateField]);
      if (isNaN(date.getTime())) return;
      const diffMonths = (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth());
      if (diffMonths >= 0 && diffMonths < 12 && (!filterFn || filterFn(item))) {
        counts[11 - diffMonths] += 1;
      }
    });
    return counts;
  };

  // Helper to compute monthly sums based on a date field
  const getMonthlySums = (items, dateField, valueSelector) => {
    const sums = Array(12).fill(0);
    const now = new Date();
    items.forEach(item => {
      if (!item[dateField]) return;
      const date = toDate(item[dateField]);
      if (isNaN(date.getTime())) return;
      const diffMonths = (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth());
      if (diffMonths >= 0 && diffMonths < 12) {
        const val = Number(valueSelector(item) || 0);
        if (!isNaN(val)) sums[11 - diffMonths] += val;
      }
    });
    return sums;
  };

  const monthlyLoans = getMonthlyCounts(statsLoans, 'paid_out_on');
  // The running loans' total_due, by the month each was paid out.
  const monthlyOutstanding = getMonthlySums(outstandingLoans, 'paid_out_on', l => Number(l.total_due) || 0);
  const monthlyPaid = getMonthlyCounts(statsLoans, 'paid_out_on', l => l.status === 'Paid Off');
  // Rejected loans were never paid out: by the month applied for.
  const monthlyRejected = getMonthlyCounts(loans, 'created_at', l => l.status === 'Rejected');
  const monthlyAmountTaken = getMonthlySums(statsLoans, 'paid_out_on', l => parseFloat(l.principal_amount) || 0);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'index', intersect: false },
    },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, grid: { color: '#e5e7eb' } },
    },
  };

  const loansChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: 'Loans Taken',
        data: monthlyLoans,
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37,99,235,0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const outstandingChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: 'Outstanding Loans',
        data: monthlyOutstanding,
        borderColor: '#eab308',
        backgroundColor: 'rgba(234,179,8,0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const paidChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: 'Paid Loans',
        data: monthlyPaid,
        borderColor: '#22c55e',
        backgroundColor: 'rgba(34,197,94,0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const amountTakenChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: 'Amount Taken',
        data: monthlyAmountTaken,
        borderColor: '#22c55e',
        backgroundColor: 'rgba(34,197,94,0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const rejectedChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: 'Rejected Loans',
        data: monthlyRejected,
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239,68,68,0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Filter loans for table/card display based on search term; keep stats/charts based on all loans
  const filteredLoansBase = loans.filter(l => {
    const values = [
      l.id?.toString() || '',
      l.loan_code || '',
      l.status || '',
      l.application_type || '',
      l.loan_reason_name || '',
      l.principal_amount?.toString() || '',
      l.principal_amount_plus_interest?.toString() || '',
      l.interest_rate?.toString() || '',
      l.duration_months?.toString() || '',
      l.monthly_payment?.toString() || '',
      l.due_date || '',
      l.outstanding?.toString() || ''
    ].join(' ').toLowerCase();
    return values.includes((searchTerm || '').toLowerCase());
  });

  const filteredLoans = filteredLoansBase;

  // Apply sorting
  const sortedLoans = [...filteredLoans].sort((a, b) => {
    const dir = sortBy.dir === 'asc' ? 1 : -1;
    switch (sortBy.key) {
      case 'principal':
        return ((parseFloat(a.principal_amount)||0) - (parseFloat(b.principal_amount)||0)) * dir;
      case 'outstanding':
        return ((parseFloat(a.outstanding)||0) - (parseFloat(b.outstanding)||0)) * dir;
      case 'due_date':
        return ((new Date(a.due_date||0)).getTime() - (new Date(b.due_date||0)).getTime()) * dir;
      default:
        return ((a.id||0) - (b.id||0)) * dir;
    }
  });

  const totalPages = Math.max(1, Math.ceil(sortedLoans.length / PAGE_SIZE));
  // A click on a column header sorts every loan, then the page is taken.
  const loanTable = useSortableTable(sortedLoans);
  const paginatedLoans = loanTable.rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Reset to first page when search changes
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  const handleShowLoanDetails = (loan) => {
    setSelectedLoan(loan);
    setShowLoanDetails(true);
  };

  const handleCloseLoanDetails = () => {
    setShowLoanDetails(false);
    setSelectedLoan(null);
  };

  // Open the signed contract document in a new tab (fallback to view-only modal if not found)
  const openSignedContract = async (loan) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setActionMessage('Please log in to view the contract.');
        setActionType('warning');
        return;
      }
      // Fetch loan documents and find the signed contract
      const docsRes = await axios.get(apiUrl(`/api/document/loan/${loan.id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const documents = Array.isArray(docsRes.data)
        ? docsRes.data
        : (docsRes.data && Array.isArray(docsRes.data.documents))
          ? docsRes.data.documents
          : [];

      // Prefer explicit type; otherwise fallback to anything that looks like a signed contract
      const signed = documents.find(d => (d.document_type || '').toLowerCase() === 'signed contract')
        || documents.find(d => /signed|contract|agreement/i.test(d.document_type || d.file_name || ''))
        || null;

      if (signed && signed.id) {
        try {
          await openDocument(signed.id);
        } catch (e) {
          setActionMessage('Could not open signed contract.');
          setActionType('error');
        }
        return;
      }

      // If no signed contract found, fallback to read-only contract preview
      setActionMessage('Signed contract not found yet. Showing contract preview.');
      setActionType('info');
      openContractModal(loan, true);
    } catch (err) {
      console.warn('Failed to open signed contract, falling back to preview:', err);
      setActionMessage('Could not open signed contract. Showing contract preview.');
      setActionType('warning');
      openContractModal(loan, true);
    }
  };

  const openContractModal = async (loan, viewOnly = false) => {
    try {
      // If not viewOnly, only open contract modal for online pending loans (signing flow)
      // Also allow 'Awaiting Client' for contract review by client
      if (!viewOnly) {
        // Whether the client can sign this loan's agreement is the API's call.
        if (!loan.state?.actions?.sign) {
          setActionMessage(`This loan is ${loan.status}, so its agreement cannot be signed now.`);
          setActionType('info');
          return;
        }
      }

      const token = localStorage.getItem('token');
      
      // Set loading message
      setActionMessage('Loading contract preview...');
      setActionType('info');

      // Check if loan already has signatures
      try {
        // Optional: check if a signed contract already exists
  const docsRes = await axios.get(apiUrl(`/api/document/loan/${loan.id}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Check if documents data is in the expected format (array or documents property)
        const documents = Array.isArray(docsRes.data) ? docsRes.data : 
                         (docsRes.data && Array.isArray(docsRes.data.documents)) ? docsRes.data.documents : [];
                         
        const hasContract = documents.some(d => d.document_type === 'Signed Contract');
        if (hasContract) {
          setActionMessage('Contract already submitted for this loan.');
          setActionType('info');
          return;
        }
      } catch (docErr) {
        // Continue even if document check fails
        console.warn('Error checking documents, continuing with contract modal:', docErr);
      }

      // Get client profile data
  const profileRes = await axios.get(apiUrl('/api/clients/my-profile'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Client profile loaded:', profileRes.data);
      console.log('Selected loan for contract:', loan);
      
      // Set up the contract modal
      setClientProfile(profileRes.data);
      setSelectedLoanForContract(loan);
      setReferences({ reference1_name: '', reference1_tel: '', reference2_name: '', reference2_tel: '' });
      setContractMessage('');
      setShowContractModal(true);
      setActionMessage(''); // Clear the loading message
    } catch (err) {
      console.error('Error opening contract modal:', err);
      setActionMessage('Failed to start contract review: ' + (err.response?.data?.error || err.message));
      setActionType('error');
    }
  };

  const handleGenerateAndUpload = async (clientDataWithSignature) => {
    if (!selectedLoanForContract || !clientProfile) {
      console.error('Missing required data for contract submission');
      setContractMessage('Missing loan or client data. Please try again.');
      return;
    }
    
    // Validate required fields. The agreement has no Witness 1: the
    // borrower's signature and initials only.
    if (!clientDataWithSignature.signature || !clientDataWithSignature.initials?.borrowerInitials) {
      setContractMessage('Your signature and initials are both required.');
      return;
    }
    
    if (!clientDataWithSignature.reference1_name || !clientDataWithSignature.reference1_tel ||
        !clientDataWithSignature.reference2_name || !clientDataWithSignature.reference2_tel) {
      setContractMessage('Please provide all reference information.');
      return;  
    }
    
    setIsGeneratingContract(true);
    try {
      console.log('Submitting client signatures for online loan ID:', selectedLoanForContract.id);
      console.log('Client data with signatures:', {
        ...clientDataWithSignature,
        signature: clientDataWithSignature.signature ? 'PRESENT' : 'MISSING',
        initials: clientDataWithSignature.initials ? {
          borrowerInitials: clientDataWithSignature.initials.borrowerInitials ? 'PRESENT' : 'MISSING'
        } : 'MISSING'
      });
      
      const token = localStorage.getItem('token');

      // The live photo (holding the ID) goes onto the loan BEFORE signing:
      // a signed contract always has it, and the API refuses to sign without
      // one. Only taken when the loan does not have one yet.
      let photoToUpload = requiredPhotoFile;
      if (!photoToUpload && requiredPhotoPreview) {
        try {
          const res = await fetch(requiredPhotoPreview);
          const blob = await res.blob();
          photoToUpload = new File([blob], `client_photo_${selectedLoanForContract.id}.jpg`, { type: blob.type || 'image/jpeg' });
        } catch (convErr) {
          console.error('Failed to convert preview to file:', convErr);
          setContractMessage('Failed to prepare captured photo for upload. Please recapture and try again');
          setIsGeneratingContract(false);
          return;
        }
      }
      if (photoToUpload) {
        try {
          const formData = new FormData();
          formData.append('contract', photoToUpload);
          formData.append('loan_id', selectedLoanForContract.id);
          formData.append('client_id', clientProfile.personal_info?.id);
          formData.append('document_type', 'Online Client Photo');
          const upRes = await fetch(apiUrl('/api/document/upload'), {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData
          });
          if (!upRes.ok) {
            // A refused file (too large, wrong type) comes back with the API's message.
            const body = await upRes.json().catch(() => ({}));
            const upErr = new Error(body?.error || 'Failed to upload your live photo. Please try again');
            upErr.apiMessage = body?.error || null;
            throw upErr;
          }
          setRequiredPhotoFile(null);
          setRequiredPhotoPreview(null);
        } catch (upErr) {
          console.error('Failed to upload client photo:', upErr);
          setContractMessage(upErr?.apiMessage || 'Failed to upload your live photo. Please try again');
          setIsGeneratingContract(false);
          return;
        }
      }

      // Send client signatures and references to queue; no PDF generation here
      const response = await axios.post(
  apiUrl(`/api/loans/${selectedLoanForContract.id}/client-sign`),
        {
          references: {
            reference1_name: clientDataWithSignature.reference1_name,
            reference1_tel: clientDataWithSignature.reference1_tel,
            reference2_name: clientDataWithSignature.reference2_name,
            reference2_tel: clientDataWithSignature.reference2_tel,
          },
          signatureLocations: clientDataWithSignature.signatureLocations,
          // How the money is paid out once approved (mandatory).
          payout: clientDataWithSignature.payout,
          signature: clientDataWithSignature.signature, // borrower signature
          initials: clientDataWithSignature.initials, // includes borrowerInitials & witness1Initials
          witness1Signature: clientDataWithSignature.witness1Signature,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Server response:', response.data);

      setShowContractModal(false);
      setSelectedLoanForContract(null);
      setContractMessage('');
      setActionMessage('Contract signed successfully! Submitted to staff for review and finalization.');
      setActionType('success');
      
      // Refresh loans to reflect new status "Awaiting Staff Signature"
      try {
        await loadLoans();
      } catch (refreshError) {
        console.error('Error refreshing loans after submission:', refreshError);
      }
    } catch (err) {
      console.error('Error submitting signatures:', err);
      setContractMessage(
        err.response?.data?.error || 'Failed to submit signatures for staff review'
      );
    }
    setIsGeneratingContract(false);
  };

  const getMessageClass = () => {
    switch (actionType) {
      case 'success':
        return 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700/50';
      case 'error':
        return 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700/50';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700/50';
      default:
        return 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700/50';
    }
  };

  // Modal state uses shared InvoiceModal; no need for local extract/HTML here


  // Use shared enrichment util


  // Use shared renderInvoiceHtml

  const openPrintableInvoice = async (loan) => {
    try {
      const enriched = await enrichInvoiceLoan(loan);
      const html = renderInvoiceHtml(enriched);
      const w = window.open('', '_blank');
      if (!w) { alert('Popup blocked. Please allow popups for this site to print or download invoices.'); return; }
      w.document.open();
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => { w.print(); }, 500);
    } catch (err) {
      console.error('Failed to open printable invoice:', err);
      alert('Failed to open printable invoice');
    }
  };

  const downloadInvoiceHtml = async (loan) => {
    try {
      const enriched = await enrichInvoiceLoan(loan);
      const html = renderInvoiceHtml(enriched);
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_loan_${enriched.id}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download invoice HTML:', err);
      alert('Failed to download invoice HTML');
    }
  };
  
  const downloadInvoicePdf = async (loan) => {
    let wrapper = null;
    try {
      const enriched = await enrichInvoiceLoan(loan);
      const html = renderInvoiceHtml(enriched);

      setActionMessage('Generating PDF...');
      setActionType('info');

      // Parse invoice HTML and inject into main document to avoid
      // cross-document html2canvas capture issues with iframes.
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const pageEl  = parsed.querySelector('.page');
      const styleEl = parsed.querySelector('style');
      if (!pageEl) {
        setActionMessage('PDF generation failed – page element not found.');
        setActionType('error');
        return;
      }

      wrapper = document.createElement('div');
      wrapper.style.cssText =
        'position:fixed;left:-9999px;top:0;width:820px;pointer-events:none;background:#e2e8f0';
      if (styleEl) {
        const s = document.createElement('style');
        s.textContent = styleEl.textContent;
        wrapper.appendChild(s);
      }
      wrapper.appendChild(pageEl.cloneNode(true));
      document.body.appendChild(wrapper);

      const target = wrapper.querySelector('.page');

      // Allow a paint cycle before capture
      setTimeout(() => {
        const opts = {
          margin:      [0, 0, 0, 0],
          filename:    `invoice_loan_${enriched.id}.pdf`,
          image:       { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: '#e2e8f0',
            windowWidth: 820,
          },
          jsPDF:     { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        };
        html2pdf().from(target).set(opts).save()
          .then(() => {
            if (wrapper && wrapper.parentNode) document.body.removeChild(wrapper);
            setActionMessage('PDF generated successfully!');
            setActionType('success');
            setTimeout(() => setActionMessage(''), 3000);
          })
          .catch(err => {
            if (wrapper && wrapper.parentNode) document.body.removeChild(wrapper);
            console.error('Failed to generate PDF:', err);
            setActionMessage('Failed to generate PDF. Please try again.');
            setActionType('error');
          });
      }, 300);
    } catch (err) {
      if (wrapper && wrapper.parentNode) document.body.removeChild(wrapper);
      console.error('Failed to generate PDF:', err);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // Open invoice modal but ensure client contact/address are populated by fetching client profile if needed
  const openInvoiceModal = async (loan) => {
    try {
      const enriched = await enrichInvoiceLoan(loan);
      setInvoiceLoan(enriched);
      setShowInvoiceModal(true);
    } catch (err) {
      console.error('Error opening invoice modal', err);
      setInvoiceLoan(loan);
      setShowInvoiceModal(true);
    }
  };

  return (
    <div className="max-w-full min-h-screen">
      <h1 className="text-3xl sm:text-4xl font-extrabold text-blue-800 dark:text-blue-300 mb-2 sm:mb-4 border-b-2 border-blue-200 dark:border-slate-700 pb-1 sm:pb-2">
        Client Dashboard
      </h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <ChartCard label="Total Loans Taken" value={totalLoans} color="blue" onClick={() => setSearchTerm('')} title="Show all my loans">
          <Line data={loansChartData} options={chartOptions} height={80} />
        </ChartCard>
        <ChartCard label="Total Amount Taken" value={toCurrency(totalAmount)} color="green">
          <Line data={amountTakenChartData} options={chartOptions} height={80} />
        </ChartCard>
        {/* With one loan running, the outstanding total is that loan's: it opens it. */}
        <ChartCard label="Total Outstanding" value={toCurrency(totalOutstanding)} color="amber"
          onClick={outstandingLoans.length === 1 ? () => handleShowLoanDetails(outstandingLoans[0]) : null}
          title={outstandingLoans.length === 1 ? 'Open the loan' : undefined}>
          <Line data={outstandingChartData} options={chartOptions} height={80} />
        </ChartCard>
        <ChartCard label="Rejected Loans" value={loans.filter(l => l.status === 'Rejected').length} color="red" onClick={() => setSearchTerm('Rejected')} title="Show my rejected loans">
          <Line data={rejectedChartData} options={chartOptions} height={80} />
        </ChartCard>
      </div>

      {/* Alerts strip (overdue/due soon) */}
      <DueAlertsStrip summary={summary} loans={loans} onViewInvoice={openInvoiceModal} />

      {/* A loan application saved to finish later, if there is one (the
          summary's saved_application; loaded by the card on an older API) */}
      {summary && (
        <SavedApplicationCard
          key={loansRefreshKey}
          saved={'saved_application' in summary ? summary.saved_application : undefined}
          onContinue={() => openApply(true)}
          onDiscarded={() => setLoansRefreshKey((k) => k + 1)}
        />
      )}

      {/* A loan staff sent back needs the client's attention: shown first */}
      <SentBackBanner loans={loans} onReview={openContractModal} />

      {/* Next Payment + Application Stepper row (above search/pagination) */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
        {/* Apply only when every loan is finished and no application is
            saved (the API's show_apply); otherwise the loan that is in the
            way, or nothing (the saved application above is the way on). */}
        {(() => {
          if (!summary) return null;
          const blockingId = summary.blocking_loan?.id;
          const blocking = !summary.can_apply
            ? (loans.find(l => l.id === blockingId) || summary.blocking_loan)
            : null;
          if (blocking) return <CurrentLoanCard loan={blocking} current={summary.current_loan} onView={handleShowLoanDetails} />;
          const showApply = summary.show_apply !== undefined ? !!summary.show_apply : !!summary.can_apply;
          return showApply ? <ApplyForLoanCard onApply={() => openApply(false)} /> : null;
        })()}
        <NextPaymentCard
          summary={summary}
          loans={loans}
          onViewInvoice={openInvoiceModal}
          onDownloadPdf={downloadInvoicePdf}
          onDownloadHtml={downloadInvoiceHtml}
          onViewLoan={handleShowLoanDetails}
          fallback={<NoPaymentCard summary={summary} onView={(l) => handleShowLoanDetails(loans.find(x => x.id === l.id) || l)} />}
        />
        <ApplicationStepper loans={loans} summary={summary} onContinue={openContractModal} fallback={<LoanSummaryCard loans={loans} summary={summary} onView={handleShowLoanDetails} />} />
      </div>

      {/* Loan details / invoice panel — opens directly below the alerts and
          Next Payment section instead of at the bottom of the page */}
      {((showLoanDetails && selectedLoan) || (showInvoiceModal && invoiceLoan)) && (
        <div ref={detailsPanelRef} className="mb-4 scroll-mt-4">
          {showLoanDetails && selectedLoan && (
            <LoanDetailsPopout loan={selectedLoan} onClose={handleCloseLoanDetails} />
          )}
          {showInvoiceModal && invoiceLoan && (
            <InvoiceModal
              visible={showInvoiceModal}
              loan={invoiceLoan}
              onClose={() => { setShowInvoiceModal(false); setInvoiceLoan(null); }}
            />
          )}
        </div>
      )}

      {actionMessage && (
        <div className={`mb-2 p-3 rounded border flex items-center justify-between ${getMessageClass()}`}>
          <span>{actionMessage}</span>
          <button
            onClick={() => setActionMessage('')}
            className="ml-4 px-2 py-1 rounded-full text-lg font-bold hover:bg-gray-200 dark:hover:bg-slate-700 focus:outline-none"
            aria-label="Close"
            title="Close"
          >
            &times;
          </button>
        </div>
  )}
      {error && <div className="p-2 rounded-lg mb-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">{error}</div>}

    {/* Loans (left) beside the documents hub (right) */}
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
    <div className="space-y-2 min-w-0">
      {/* Loan filters and paging, above the loans */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <SearchBox
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search loans..."
            debounceMs={400}
            storageKey="dash.search"
            className="w-full sm:w-64"
          />
        </div>

        <Pager page={currentPage} pages={totalPages} onPage={setCurrentPage} />
      </div>
    <div className="w-full overflow-x-auto">
            <table className="w-full min-w-full border-collapse">
              <thead>
                <tr className={TABLE_HEAD_ROW}>
                  {loanTable.th('Loan Code', 'code', (l) => l.loan_code || l.loanCode, LOAN_TH)}
                  {loanTable.th('Status', 'status', (l) => l.status, LOAN_TH)}
                  {loanTable.th('Principal', 'principal', (l) => Number(l.principal_amount), LOAN_TH_RIGHT)}
                  {loanTable.th('Outstanding', 'outstanding', (l) => l.total_due, LOAN_TH_RIGHT)}
                  <th scope="col" className={`px-3 py-2 text-left ${TABLE_HEAD_TEXT} whitespace-nowrap`}>Actions</th>
                </tr>
              </thead>
              <tbody>
            {paginatedLoans.length === 0 && (
              <tr className={TABLE_ROW_LINE}>
                <td colSpan={5} className="px-3 py-4 text-center text-sm text-gray-500 dark:text-slate-400 bg-white dark:bg-slate-900">No results found.</td>
              </tr>
            )}
            {paginatedLoans.map((loan, idx) => {
              return (
              <tr
                key={loan.id}
                {...clickableProps(() => handleShowLoanDetails(loan), `Open loan ${loan.loan_code || loan.id}`)}
                className={`${TABLE_ROW_LINE} ${idx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-900'} cursor-pointer hover:bg-blue-50 dark:hover:bg-slate-700 focus:outline-none focus-visible:bg-blue-50 transition-colors duration-100`}
              >
                <td className="px-3 py-2 text-xs text-gray-900 dark:text-slate-100 whitespace-nowrap">
                  <span className="inline-flex items-center">
                    {loan.status === 'Pending' && loan.application_type === 'Online' && (
                      <span className="mr-2 h-4 w-1 bg-yellow-500 rounded-full"></span>
                    )}
                    <span className={`font-medium ${LINK_TEXT}`}>{loan.loan_code || loan.loanCode || '-'}</span>
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-gray-900 dark:text-slate-100">
                  <StatusBadge status={loan.status} size="small" />
                </td>
                <td className="px-3 py-2 text-xs text-gray-900 dark:text-slate-100 text-right whitespace-nowrap">
                  {toCurrency(loan.principal_amount)}
                  {Number(loan.monthly_payment) > 0 && (
                    <div className="text-[11px] text-gray-500 dark:text-slate-400">{toCurrency(loan.monthly_payment)} / month</div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-gray-900 dark:text-slate-100 text-right whitespace-nowrap">{
                  // What is still owed, penalties included: the API's figure.
                  // It opens the invoice when there is one to see.
                  loan.total_due != null && Number.isFinite(Number(loan.total_due))
                    ? (loan.state?.actions?.view_invoice === true
                      ? <button type="button" className={LINK_TEXT} title="Open the invoice"
                          onClick={(e) => { e.stopPropagation(); openInvoiceModal(loan); }}>
                          {toCurrency(loan.total_due)}
                        </button>
                      : toCurrency(loan.total_due))
                    : '-'
                }
                  {loan.due_date && (
                    <div className="text-[11px] text-gray-500 dark:text-slate-400">Due {formatDateDMY(loan.due_date)}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-gray-900 dark:text-slate-100" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  <ActionsCell>
                      {(() => {
                        // What the client can do with the loan: the API's call (loan.state.actions).
                        const actions = loan.state?.actions || {};
                        const canResubmit = actions.resubmit === true;
                        const canSignContract = actions.sign === true && !canResubmit;
                        const canContractReview = actions.view_contract === true;
                        const canViewContract = actions.view_contract === true;
                        const canViewInvoice = actions.view_invoice === true;
                        const canShowReason = loan.status === 'Rejected';
    
                        return (
                          <>
                            <button
                              onClick={() => handleShowLoanDetails(loan)}
                              className="px-2.5 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300"
                            >
                              View Loan
                            </button>
                            {canResubmit && (
                              <ActionButton
                                onClick={() => openContractModal(loan)}
                                className="px-2.5 py-1 text-xs font-medium rounded-md bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300"
                              >
                                Continue Application
                              </ActionButton>
                            )}
                            {canSignContract && (
                              <ActionButton
                                onClick={() => openContractModal(loan)}
                                className="px-2.5 py-1 text-xs font-medium rounded-md bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300"
                              >
                                Sign Contract
                              </ActionButton>
                            )}
                            {canContractReview && (
                              <ActionButton
                                onClick={() => openContractModal(loan, true)}
                                className="px-2.5 py-1 text-xs font-medium rounded-md bg-purple-50 text-purple-600 hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-300"
                              >
                                Contract Review
                              </ActionButton>
                            )}
                            {/* Show View Contract for loans that are not in these intermediate/restricted statuses */}
                            {canViewContract && (
                              <ActionButton
                                onClick={() => openSignedContract(loan)}
                                className="px-2.5 py-1 text-xs font-medium rounded-md bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300"
                              >
                                View Contract
                              </ActionButton>
                            )}
                            {/* View Invoice - only when loan is not in these intermediate statuses */}
                            {canViewInvoice && (
                              <ActionButton
                                onClick={() => openInvoiceModal(loan)}
                                className="px-2.5 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300"
                              >
                                View Invoice
                              </ActionButton>
                            )}
                            {canShowReason && (
                              <button
                                onClick={() => {
                                  setActionMessage(loan.rejection_reason ? loan.rejection_reason : 'No reason provided');
                                  setActionType('error');
                                }}
                                className="px-2.5 py-1 text-xs font-medium rounded-md bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300"
                              >
                                Reason
                              </button>
                            )}
                          </>
                        );
                      })()}
                  </ActionsCell>
                </td>
              </tr>
              );
            })}
              </tbody>
            </table>
    </div>
    </div>

      <DocumentsHubCard loans={loans} onContinue={openContractModal} />
    </div>

      {applyOpen && (
        <FullScreenPanel isOpen onClose={closeApply} title="Apply for a loan" fullHeight>
          <LoanApplication
            embedded
            resume={applyResume}
            onSavedForLater={() => {
              closeApply();
              setActionType('success');
              setActionMessage('Your application is saved. Continue it from here any time to finish it — you will only need to take your photo again.');
            }}
            onReviewSent={(message) => {
              closeApply();
              setActionType('success');
              setActionMessage(message);
            }}
          />
        </FullScreenPanel>
      )}

      {showContractModal && selectedLoanForContract && clientProfile && (
        <FullScreenPanel
          isOpen
          onClose={() => setShowContractModal(false)}
          title="Review Loan Agreement"
          subtitle={[selectedLoanForContract.loan_code, selectedLoanForContract.application_type, selectedLoanForContract.status].filter(Boolean).join(' · ')}
        >
          <div>
                  {/* Client-visible notes and attached documents (rendered above the contract card) */}
                  {/* What staff asked for, then the loan exactly as on its
                      Loan Details screen (the same component), then the
                      contract the client saw at first signing. */}
                  {showContractModal && selectedLoanForContract && (
                    <>
                    <ClientDocumentsAndNotes
                      part="notes"
                      loanId={selectedLoanForContract.id}
                      clientId={clientProfile.personal_info?.id}
                      loanActions={selectedLoanForContract.state?.actions || {}}
                      requiredPhotoFile={requiredPhotoFile}
                      requiredPhotoPreview={requiredPhotoPreview}
                      setRequiredPhotoFile={setRequiredPhotoFile}
                      setRequiredPhotoPreview={setRequiredPhotoPreview}
                    />
                    <div className="mb-6">
                      <LoanDetails
                        embedded
                        loan={selectedLoanForContract}
                        onClose={() => {}}
                        canApprove={false}
                        canReject={false}
                        canRevertToPending={false}
                        userRole={'client'}
                        inDocuments={(
                          <ClientDocumentsAndNotes
                            part="photo"
                            loanId={selectedLoanForContract.id}
                            clientId={clientProfile.personal_info?.id}
                            loanActions={selectedLoanForContract.state?.actions || {}}
                            requiredPhotoFile={requiredPhotoFile}
                            requiredPhotoPreview={requiredPhotoPreview}
                            setRequiredPhotoFile={setRequiredPhotoFile}
                            setRequiredPhotoPreview={setRequiredPhotoPreview}
                          />
                        )}
                      />
                    </div>
                    </>
                  )}
              {contractMessage && (
                <div ref={contractMessageRef} role="alert" className="mb-6 p-4 rounded-lg border flex items-center justify-between bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700/50">
                  <span>{contractMessage}</span>
                  <button
                    onClick={() => setContractMessage('')}
                    className="ml-4 px-2 py-1 rounded-full text-lg font-bold hover:bg-gray-200 dark:hover:bg-slate-700 focus:outline-none"
                    aria-label="Close"
                  >×</button>
                </div>
              )}
              <ContractPreview
                loanData={selectedLoanForContract}
                clientData={{
                  id: clientProfile.personal_info?.id,
                  first_name: clientProfile.personal_info?.first_name,
                  middle_name: clientProfile.personal_info?.middle_name,
                  last_name: clientProfile.personal_info?.last_name,
                  id_passport_no: clientProfile.personal_info?.id_passport_no,
                  email: clientProfile.personal_info?.email,
                  phone_number: clientProfile.personal_info?.phone_number,
                  postal_address: clientProfile.personal_info?.postal_address,
                  residential_address: clientProfile.personal_info?.residential_address,
                  marital_status: clientProfile.personal_info?.marital_status,
                  occupation: clientProfile.occupation_info?.occupation,
                  employer_name: clientProfile.occupation_info?.employer_name,
                  payslip_number: clientProfile.occupation_info?.payslip_number,
                  employer_address: clientProfile.occupation_info?.employer_address,
                  employer_phone: clientProfile.occupation_info?.employer_phone,
                  bank_name: clientProfile.bank_info?.bank_name,
                  account_number: clientProfile.bank_info?.account_number,
                  branch_code: clientProfile.bank_info?.branch_code,
                  account_type: clientProfile.bank_info?.account_type
                }}
                onEdit={() => setShowContractModal(false)}
                onGenerate={handleGenerateAndUpload}
                loading={isGeneratingContract}
                references={references}
                setReferences={setReferences}
                requiredPhotoFile={requiredPhotoFile}
                requiredPhotoPreview={requiredPhotoPreview}
                setRequiredPhotoFile={setRequiredPhotoFile}
                setRequiredPhotoPreview={setRequiredPhotoPreview}
                // The same review as at first signing: a loan the client may
                // sign or resubmit (the API's state.actions) opens with the
                // loan details, the agreement, the references and the
                // signature pads. Only a loan past that stage is read-only.
                readOnly={!(selectedLoanForContract?.state?.actions?.sign === true || selectedLoanForContract?.state?.actions?.resubmit === true)}
                showPreviewHeader={false}
                initialValues={selectedLoanForContract?.signatures || {}}
              />
          </div>
        </FullScreenPanel>
      )}

      {/* Page-level invoice debug panel (toggle with localStorage.invoice_debug = '1') */}
      {typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('invoice_debug') === '1' && (
        <div style={{ position: 'fixed', right: 12, bottom: 12, width: 420, maxHeight: '60vh', overflow: 'auto', background: 'white', border: '1px solid #e5e7eb', padding: 12, zIndex: 9999, borderRadius: 8 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Invoice Debug Panel</div>
          <div style={{ fontSize: 12, color: '#444', marginBottom: 6 }}>invoiceLoan:</div>
          <pre style={{ fontSize: 11, maxHeight: 180, overflow: 'auto', background: '#f9fafb', padding: 8 }}>{JSON.stringify(invoiceLoan, null, 2)}</pre>
          <div style={{ fontSize: 12, color: '#444', marginTop: 8, marginBottom: 6 }}>lastFetchedClient:</div>
          <pre style={{ fontSize: 11, maxHeight: 180, overflow: 'auto', background: '#f9fafb', padding: 8 }}>{JSON.stringify(lastFetchedClient, null, 2)}</pre>
          <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => { window.localStorage.removeItem('invoice_debug'); window.location.reload(); }} className="px-2 py-1 text-xs border rounded">Disable</button>
          </div>
        </div>
      )}
    </div>
  );
};


export default ClientDashboard;